import { useCallback, useRef, useState } from 'react';
import { pdfAPIService } from '../services/pdf-api-service';
import { bidAnalysisService, type BidAnalysisResult } from '../services/bid-analysis-service';
import { bidApiService } from '../services/bid-api-service';
import { bidAttachmentService } from '../services/bid-attachment-service';
import { trackBidCreated } from '@/lib/posthog-events';

export type PdfUploadStatus = 'uploading' | 'analyzing' | 'needs_project' | 'saving' | 'completed' | 'error';

export interface PdfUpload {
  id: string;
  file: File;
  status: PdfUploadStatus;
  progress: number;
  /** what the AI read from the PDF */
  analysis?: BidAnalysisResult;
  /** shown when the bid was created but something around it did not work (e.g. the PDF could not be attached) */
  warning?: string;
  error?: string;
}

interface Options {
  /** The project the user is working in (the global project selector), '' when "All Projects". */
  contextProjectId: string;
  session: { companyId: string | null; userId: string | undefined };
  /** A bid was created from a PDF -- refresh the list. */
  onBidCreated: () => void;
}

/**
 * The "drop a bid PDF" pipeline: PDF -> text -> AI analysis (vendor, category, date, total, project) -> match the
 * company's vendor / project -> create the bid -> attach the PDF.
 *
 * A bid always belongs to a project, and a project is never invented: it is the one the user is working in, or the one
 * the PDF names (when it matches one of the company's projects). When neither exists the upload STOPS at
 * 'needs_project' -- keeping what the AI already read, so it is not paid for twice -- until the user picks the project.
 */
export function useBidPdfUpload({ contextProjectId, session, onBidCreated }: Options) {
  const [uploads, setUploads] = useState<PdfUpload[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  // handlers run long after the render that created them; they read the latest context through a ref
  const latest = useRef({ contextProjectId, session, onBidCreated, uploads });
  latest.current = { contextProjectId, session, onBidCreated, uploads };

  const patch = useCallback((id: string, changes: Partial<PdfUpload>) => {
    setUploads((prev) => prev.map((upload) => (upload.id === id ? { ...upload, ...changes } : upload)));
  }, []);

  const createBidFromAnalysis = useCallback(async (upload: PdfUpload, analysis: BidAnalysisResult, projectId: string) => {
    patch(upload.id, { status: 'saving', progress: 85, analysis, error: undefined });
    try {
      const saved = await bidApiService.createBid({
        project_id: projectId,
        vendor_id: analysis.vendor_id ?? null,
        subcontractor_name: analysis.subcontractor_name,
        category: analysis.category,
        date_received: analysis.date,
        amount: analysis.total_amount || 0,
        status: 'received',
        name: `Bid from ${analysis.subcontractor_name}`,
        description: 'Auto-extracted from PDF',
      });
      trackBidCreated({
        bid_id: saved.id,
        subcontractor_name: analysis.subcontractor_name,
        category: analysis.category,
        amount: analysis.total_amount || 0,
        status: 'received',
        project_id: projectId,
        vendor_id: analysis.vendor_id,
        source: 'pdf_upload',
      });

      let warning: string | undefined;
      const { companyId, userId } = latest.current.session;
      try {
        if (!companyId || !userId) throw new Error('your session is not ready');
        await bidAttachmentService.uploadBidAttachment(upload.file, saved.id, { companyId, userId });
      } catch (attachError) {
        warning = `The bid was created, but the PDF could not be attached: ${attachError instanceof Error ? attachError.message : 'unknown error'}. Open the bid to attach it again.`;
      }
      patch(upload.id, { status: 'completed', progress: 100, warning });
      latest.current.onBidCreated();
    } catch (error) {
      patch(upload.id, { status: 'error', progress: 0, error: error instanceof Error ? error.message : 'The bid could not be created.' });
    }
  }, [patch]);

  const processFile = useCallback(async (upload: PdfUpload) => {
    try {
      patch(upload.id, { status: 'uploading', progress: 25 });
      const pdf = await pdfAPIService.parsePDF(upload.file);

      patch(upload.id, { status: 'analyzing', progress: 55 });
      const analysis = await bidAnalysisService.analyzeBid(pdf);

      const projectId = latest.current.contextProjectId || analysis.matched_project?.id;
      if (!projectId) {
        patch(upload.id, { status: 'needs_project', progress: 70, analysis });
        return;
      }
      await createBidFromAnalysis(upload, analysis, projectId);
    } catch (error) {
      patch(upload.id, { status: 'error', progress: 0, error: error instanceof Error ? error.message : 'Unknown error' });
    }
  }, [patch, createBidFromAnalysis]);

  const handleDrop = useCallback(async (files: File[]) => {
    const fresh: PdfUpload[] = files.map((file) => ({ id: `${Date.now()}-${Math.random()}`, file, status: 'uploading', progress: 0 }));
    setUploads((prev) => [...prev, ...fresh]);
    setIsProcessing(true);
    try {
      for (const upload of fresh) await processFile(upload);
    } finally {
      setIsProcessing(false);
    }
  }, [processFile]);

  /** The user picked the project of an upload that was waiting for one. */
  const chooseProject = useCallback(async (id: string, projectId: string) => {
    const upload = latest.current.uploads.find((candidate) => candidate.id === id);
    if (!upload?.analysis || !projectId) return;
    await createBidFromAnalysis(upload, upload.analysis, projectId);
  }, [createBidFromAnalysis]);

  const dismiss = useCallback((id: string) => setUploads((prev) => prev.filter((upload) => upload.id !== id)), []);

  return { uploads, isProcessing, handleDrop, chooseProject, dismiss };
}
